'use client'

import { ErrorDialog } from '@/components/ui/dialog/ErrorDialog'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

type UnhandeledError = {
  error: unknown
  title: string
  details?: string
  errorId?: string
}

type UnhandeledErrorsStateContextValue = readonly [
  UnhandeledError[],
  Dispatch<SetStateAction<UnhandeledError[]>>,
]

const UnhandeledErrorsStateContext = createContext<UnhandeledErrorsStateContextValue>([
  [],
  () => {},
])

function useUnhandeledErrorsState() {
  return useContext(UnhandeledErrorsStateContext)
}

const SubmitErrorDialogContext = createContext<
  (error: unknown, title: string, details?: string, errorId?: string) => void
>(() => {})

export function useSubmitErrorDialog() {
  return useContext(SubmitErrorDialogContext)
}

export type ErrorDialogSlotContextProps = {
  children?: ReactNode
}

export function ErrorDialogSlotContext({ children }: ErrorDialogSlotContextProps) {
  const [unhandeledErrors, setUnhandeledErrors] = useState<UnhandeledError[]>([])
  const unhandeledErrorsState = useMemo(
    () => [unhandeledErrors, setUnhandeledErrors] as const,
    [unhandeledErrors]
  )
  const submitErrorDialog = useCallback(
    (error: unknown, title: string, details?: string, errorId?: string) =>
      setUnhandeledErrors((prev) => [{ error, title, details, errorId }, ...prev]),
    []
  )
  return (
    <UnhandeledErrorsStateContext.Provider value={unhandeledErrorsState}>
      <SubmitErrorDialogContext.Provider value={submitErrorDialog}>
        {children}
      </SubmitErrorDialogContext.Provider>
    </UnhandeledErrorsStateContext.Provider>
  )
}

export type ErrorDialogSlotProps = {
  className?: string
}

export function ErrorDialogSlot({ className }: ErrorDialogSlotProps) {
  const [unhandeledErrors, setUnhandeledErrors] = useUnhandeledErrorsState()
  const unhandeledError = unhandeledErrors.at(-1)
  if (unhandeledError === undefined) {
    return null
  }
  return (
    <ErrorDialog
      open
      onClose={() =>
        setUnhandeledErrors((prev) => {
          prev.pop()
          return [...prev]
        })
      }
      error={unhandeledError.error}
      title={unhandeledError.title}
      details={unhandeledError.details}
      errorId={unhandeledError.errorId}
      className={className}
    />
  )
}
